import { useState, useEffect, useMemo, useRef, Key, JSX } from 'react';
import { Head, usePage, router } from '@inertiajs/react';
import { useForm } from '@mantine/form';
import {
  Container,
  TextInput,
  Select,
  Button,
  Group,
  Paper,
  Title,
  Tooltip,
  SegmentedControl,
  Accordion,
  Alert,
  MantineProvider,
  createTheme,
  Text,
  MultiSelect,
  ActionIcon,
  Stack,
  Avatar,
  Box,
  Pill,
} from '@mantine/core';
import { DatePickerInput, TimeInput, Calendar } from '@mantine/dates';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { useUser } from '../../src/context/UserContext';
import { Notifications, showNotification } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconCheck,
  IconCalendarTime,
  IconUsersGroup,
  IconClock,
  IconCalendarPlus,
  IconMapPinFilled,
  IconList,
  IconLink,
  IconAlertTriangle,
  IconInfoCircle,
} from '@tabler/icons-react';
import { RichTextEditor } from '@mantine/tiptap';
import { useEditor, EditorContent } from '@tiptap/react';
import Highlight from '@tiptap/extension-highlight';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Heading from '@tiptap/extension-heading';
import Link from '@tiptap/extension-link';
import ImageUploadModal from '../../components/ImageUploadModal';
import AddressAutocomplete from '../../components/AddressAutocompleteMap';
import RecurringEventFields from '../../components/RecurringEventFields';
import timezoneData from '../../entrypoints/utilities/timezones.json';
import dayjs from 'dayjs';
import { useMediaQuery } from '@mantine/hooks';

import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';
import '../../styles/Login-form.css';
import '@mantine/tiptap/styles.css';
import '@mantine/dates/styles.css';

type Artist = {
  id: number;
  name: string;
  avatar_url: string;
};

interface User {
  id: number;
  email: string;
  username: string;
  avatar_url: string | null;
  roles: string[];
  organizer_ids?: number[];
}

interface EventType {
  id: number;
  name: string;
}

interface DanceStyle {
  id: number;
  name: string;
}

interface EventData {
  id: number;
  name: string;
  slug?: string;
  description?: string;
  address?: string;
  online_event?: boolean;
  is_recurring?: boolean;
  spans_next_day?: boolean;
  timezone?: string[];
  event_start_date?: string;
  event_end_date?: string;
  website_url?: string;
  ticket_url?: string;
  facebook_url?: string;
  event_type_ids?: number[];
  dance_style_ids?: number[];
  event_frequency_id?: number;
  tiktok_url: string;
  meetup_url: string;
  instagram_url: string;
  event_image_url: string | null;
  artists_ids?: number[];
  user_id?: number;
  event_status_id?: number;
  organizer_ids?: number[];
  event_image?: File | null;
  event_start_time?: string;
  event_end_time?: string;
}

interface EventFrequency {
  id: number;
  name: string;
}

interface RecurringEvent {
  id: number;
  title: string;
}

interface CreateEventPageProps extends PageProps, Record<string, any> {
  event: {
    id: number;
    name: string;
    slug?: string;
    description?: string;
    address?: string;
    online_event?: boolean;
    is_recurring?: boolean;
    spans_next_day?: boolean;
    timezone?: string;
    event_start_date?: string;
    event_end_date?: string;
    event_start_time?: string;
    event_end_time?: string;
    website_url?: string;
    ticket_url?: string;
    facebook_url?: string;
    event_type_ids?: number[];
    dance_style_ids?: number[];
    event_frequency_id?: number;
    tiktok_url: string;
    meetup_url: string;
    instagram_url: string;
    event_image_url: string;
    artists?: Artist[];
    artists_ids?: number[];
    dance_style?: DanceStyle[];
    recurring_event?: RecurringEvent;
    user_id?: number;
    event_status_id?: number;
    organizer_ids?: number[];
  };
  artists?: Artist[];
  event_types: EventType[];
  dance_styles: DanceStyle[];
  event_frequencies: EventFrequency[];
  organizers?: Array<[string, number]>;
  errors?: Record<string, string[]>;
  user?: Array<[string, number]>;
}

const ordinalOptions = ['1st', '2nd', '3rd', '4th', '5th', 'Last'];
const dayOptions = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function CreateEvent() {
  const [isRecurring, setIsRecurring] = useState(false);
  const [ordinalOccurrence, setOrdinalOccurrence] = useState('');
  const [dayOfWeek, setDayOfWeek] = useState('');
  const [tooltipOpened, setTooltipOpened] = useState(false);
  const [tooltipOpenedOrganizer, setTooltipOpenedOrganizer] = useState(false);
  const [artistOptionsLoaded, setArtistOptionsLoaded] = useState(false);

  const { user, refreshUser } = useUser() as { user: User | null; refreshUser: () => Promise<void> };

  const { props } = usePage<CreateEventPageProps>();

  const fetchAllArtists = async () => {
    setIsLoadingArtists(true);
    try {
      const res = await fetch('/artists/list?all=true', {
        headers: { Accept: 'application/json' },
      });
      const data = await res.json();
      setArtistOptions(data.artists);
      setArtistOptionsLoaded(true);
    } catch (err) {
      console.error('Failed to load artists', err);
    } finally {
      setIsLoadingArtists(false);
    }
  };

  const {
    event,
    errors,
    artists: propArtists,
    event_types: propEventTypes,
    dance_styles: propDanceStyles,
    event_frequencies: propEventFrequencies,
    users: propUsers,
    event_statuses: propEventStatuses,
    organizers: propOrganizers,
  } = props;

  const [eventName, setEventName] = useState('');
  const [slug, setSlug] = useState('');
  const [debouncedSlug] = useDebouncedValue(slug, 500); // 500ms debounce
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle');
  const [seoImagePreview, setSeoImagePreview] = useState<string | null>(event.event_image_url);

  const handleImageChange = (file: File) => {
    // Update your local component state:
    setValues((prevValues) => {
      const newValues = { ...prevValues, event_image: file };

      return newValues;
    });

    // Also update the form's state:
    form.setFieldValue('event_image', file);

    // Update preview:
    setSeoImagePreview(URL.createObjectURL(file));
  };

  // Create a function to parameterize the event name
  const handleEventNameChange = (e) => {
    const newEventName = e.target.value;
    form.setFieldValue('name', newEventName);

    // Generate parameterized slug
    const parameterizedSlug = newEventName
      .toLowerCase()
      .replace(/\s+/g, '-') // Replace spaces with hyphen
      .replace(/[^a-z0-9-]/g, '') // Remove all characters except lowercase letters, numbers, and hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with a single hyphen
      .replace(/^-|-$/g, ''); // Remove leading and trailing hyphens

    setSlug(parameterizedSlug);
    form.setFieldValue('slug', parameterizedSlug);
  };

  const form = useForm({
    initialValues: {
      id: event.id || null,
      name: event.name || '',
      slug: event.slug || '',
      description: event.description || '',
      address: event.address || '',
      timezone: event.timezone || '',
      online_event: event.online_event ? 'true' : 'false',
      is_recurring: event.is_recurring ? 'true' : 'false',
      spans_next_day: event.spans_next_day ? 'true' : 'false',
      event_start_date: event.event_start_date,
      event_start_time: event.event_start_time,
      event_end_time: event.event_end_time,
      event_end_date: event.event_end_date,
      website_url: event.website_url,
      ticket_url: event.ticket_url,
      facebook_url: event.facebook_url,
      instagram_url: event.instagram_url,
      meetup_url: event.meetup_url,
      tiktok_url: event.tiktok_url,
      event_type_ids: event.event_type_ids || [],
      artistIds: event.artist_ids || [],
      dance_style_ids: event.dance_style_ids || [],
      event_frequency_id: event.event_frequency_id || '',
      user_id: event.user_id || null,
      event_status_id: event.event_status_id || null,
      organizer_ids: event.organizer_ids || [],
      recurring_event_occurrences: [],
      recurring_event_attributes: {
        start_date: '',
        start_time: '',
        end_time: '',
        recurrence_pattern: '',
        num_occurrences: 0,
        re_timezone: '',
        spans_next_day: false,
        ordinal_occurrence: '',
        occurrence_day: '',
      },
    },
    validate: {
      name: (value) => (value ? null : 'Event name is required'),
      slug: (value) => (value ? null : 'Slug is required'),
      address: (value, values) =>
        values.online_event === 'true' ? null : value ? null : 'Address is required for in-person events',
      event_frequency_id: (value) => {
        // Skip for drafts
        if (isDraft) return null;

        // Check if a frequency is selected
        return value ? null : 'Event frequency is required';
      },
      description: (value) => {
        // If using the editor instance
        if (editor) {
          // Get plain text content (strips HTML tags)
          const plainText = editor.getText();

          // Check if it meets the minimum length requirement
          if (plainText.length < 20) {
            return 'Description must be at least 20 characters long';
          }
        }
        return null;
      },
      timezone: (value) => (value ? null : 'Timezone is required'),
      event_start_date: (value, values) => {
        // Skip validation for recurring events
        if (values.is_recurring === 'true') return null;

        // Regular validation
        return value ? null : 'Event start date is required';
      },

      event_start_time: (value, values) => {
        // Skip validation for recurring events
        if (values.is_recurring === 'true') return null;

        // Regular validation
        return value ? null : 'Event start time is required';
      },
      event_end_date: (value, values) => {
        if (!value || !values.event_start_date) return null;
        const start = new Date(values.event_start_date);
        const end = new Date(value);
        if (end < start) {
          return 'Event end date must be the same as or after the event start date';
        }
        return null;
      },
      event_end_time: (value, values) => {
        if (!value || !values.event_start_time || !values.event_start_date || !values.event_end_date) {
          return null;
        }

        const startDate = new Date(values.event_start_date);
        const endDate = new Date(values.event_end_date);

        const [startHour, startMinute] = values.event_start_time.split(':').map(Number);
        const [endHour, endMinute] = value.split(':').map(Number);

        // ✅ If both dates are the same, compare the time
        if (startDate.toDateString() === endDate.toDateString()) {
          if (endHour < startHour || (endHour === startHour && endMinute <= startMinute)) {
            return 'Event end time must be after event start time when the dates are the same';
          }
        } else {
          // ✅ If end date is before start date, show an error
          if (endDate < startDate) {
            return 'Event end date cannot be before event start date';
          }
        }

        return null;
      },
      event_type_ids: (value) =>
        Array.isArray(value) && value.length > 0 ? null : 'At least one event type is required',
      dance_style_ids: (value) =>
        Array.isArray(value) && value.length > 0 ? null : 'At least one dance style is required',
      website_url: (value) => {
        if (value && !validateURL(value)) {
          return 'Please enter a valid website URL starting with http:// or https://';
        }
        return null;
      },
      ticket_url: (value) => {
        if (value && !validateURL(value)) {
          return 'Please enter a valid passes URL starting with http:// or https://';
        }
        return null;
      },
      facebook_url: (value) => {
        if (value && !validateURL(value)) {
          return 'Please enter a valid Facebook URL starting with http:// or https://';
        }
        return null;
      },
      instagram_url: (value) => {
        if (value && !validateURL(value)) {
          return 'Please enter a valid Instagram URL starting with http:// or https://';
        }
        return null;
      },
      meetup_url: (value) => {
        if (value && !validateURL(value)) {
          return 'Please enter a valid Meetup URL starting with http:// or https://';
        }
        return null;
      },
      tiktok_url: (value) => {
        if (value && !validateURL(value)) {
          return 'Please enter a valid TikTok URL starting with http:// or https://';
        }
        return null;
      },
      event_image: (value, values) => {
        // Skip validation for drafts
        if (isDraft) return null;

        // For new events, require an image
        if (!event.id && !value && !croppedImage) {
          return 'Please upload an event image';
        }

        // For existing events, check if they already have an image
        if (event.id && !value && !croppedImage && !event.event_image_url) {
          return 'Please upload an event image';
        }

        return null;
      },
    },
    validateInputOnChange: true,
  });

  const DRAFT_STATUS_ID = 3;
  const isDraft = event.event_status_id === DRAFT_STATUS_ID;
  const [values, setValues] = useState<EventData>(event);
  const startTimeRef = useRef<HTMLInputElement>(null);
  const endTimeRef = useRef<HTMLInputElement>(null);
  const startPickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => startTimeRef.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );
  const endPickerControl = (
    <ActionIcon variant="subtle" color="gray" onClick={() => endTimeRef.current?.showPicker()}>
      <IconClock size={16} stroke={1.5} />
    </ActionIcon>
  );

  const isMobile = useMediaQuery('(max-width: 768px)');

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = e.target.value; // e.g., "18:09"

    form.setFieldValue('event_start_time', timeValue); // ✅ Update form values

    setValues((prevValues) => ({
      ...prevValues,
      event_start_time: timeValue, // ✅ Ensure values are updated
    }));

    form.validateField('event_end_date');
    form.validateField('event_end_time');
  };

  const handleEndTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = e.target.value; // e.g., "16:09"

    form.setFieldValue('event_end_time', timeValue); // ✅ Update form values

    setValues((prevValues) => ({
      ...prevValues,
      event_end_time: timeValue, // ✅ Ensure values are updated
    }));

    form.validateField('event_end_date');
    form.validateField('event_end_time');
  };

  const TIMEZONE_OPTIONS = Object.entries(timezoneData).map(([value, label]) => ({
    value,
    label: `${label} (${value})`,
  }));

  function validateURL(input: string) {
    const urlPattern = /^(http:\/\/|https:\/\/)/i;
    return urlPattern.test(input);
  }

  const users = Array.isArray(propUsers) ? propUsers : [];
  const event_types = Array.isArray(propEventTypes) ? propEventTypes : [];

  const dance_styles = Array.isArray(propDanceStyles) ? propDanceStyles : [];
  const event_frequencies = Array.isArray(propEventFrequencies) ? propEventFrequencies : [];
  const event_statuses = Array.isArray(propEventStatuses) ? propEventStatuses : [];

  const filteredOrganizers = useMemo(() => {
    const organizers = user?.roles?.includes('SuperAdmin')
      ? propOrganizers || []
      : (propOrganizers || []).filter((org) => user?.organizer_ids?.includes(org.id));

    // Sort organizers alphabetically by name
    return [...organizers].sort((a, b) => a.name.localeCompare(b.name));
  }, [propOrganizers, user]);

  const is_superadmin = user?.roles?.includes('SuperAdmin') || false;

  const frequencyOrder = ['One Time', 'Weekly', 'Every Two Weeks', 'Monthly', 'Annual'];

  const sortedEventFrequencies = event_frequencies.sort((a, b) => {
    return frequencyOrder.indexOf(a.name) - frequencyOrder.indexOf(b.name);
  });

  const [overlayVisible, setOverlayVisible] = useState(false);

  const sortedDanceStyles = dance_styles.sort((a, b) => a.name.localeCompare(b.name));

  const sortedEventTypes = event_types.sort((a, b) => a.name.localeCompare(b.name));

  const sortedOrganizers = filteredOrganizers.sort((a, b) => a.name.localeCompare(b.name));

  const theme = createTheme({
    primaryColor: 'orange',
    colors: {
      orange: [
        '#FEF5ED',
        '#FDE3D1',
        '#FCB693',
        '#FA9D73',
        '#F87F4D',
        '#F66A33',
        '#EA580C',
        '#C44B0A',
        '#9E3D08',
        '#783006',
      ],
    },
    fontFamily: 'Poppins, sans-serif',
    headings: { fontFamily: 'Poppins, sans-serif' },
    components: {
      InputLabel: {
        styles: (theme: { fontSizes: { sm: any } }) => ({
          label: {
            color: '#FFF7ED', // Ensure this color applies
            fontSize: theme.fontSizes.sm, // Optional: Adjust size
            fontWeight: 500, // Optional: Adjust weight
          },
        }),
      },
      Input: {
        styles: () => ({
          input: {
            backgroundColor: '#686868', // Correctly sets input background
            color: '#F2F2F2', // Ensures text inside the input is visible
            borderColor: '#787878', // Optional: Adjust border color
            '&::placeholder': {
              color: '#fff', // Optional: Lighten placeholder text
            },
          },
        }),
      },
    },
  });

  // Helper function to format GMT offset
  const formatOffset = (offsetSeconds: number) => {
    const hours = Math.floor(Math.abs(offsetSeconds) / 3600);
    const minutes = Math.floor((Math.abs(offsetSeconds) % 3600) / 60);
    const sign = offsetSeconds >= 0 ? '+' : '-';
    return `GMT${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const fetchTimezones = async () => {
      setIsLoadingTimezones(true);
      try {
        const apiKey = 'N2IE9BKU2EQR'; // Your TimeZoneDB API key
        const response = await fetch(`https://api.timezonedb.com/v2.1/list-time-zone?key=${apiKey}&format=json`);
        const data = await response.json();

        if (data.status === 'OK' && data.zones && Array.isArray(data.zones)) {
          // Format the timezone options
          const options = data.zones.map((zone: any) => ({
            value: zone.zoneName, // IANA timezone ID (e.g., "America/Chicago")
            label: `${zone.zoneName} (${formatOffset(zone.gmtOffset)})`, // Display name with GMT offset
          }));

          setTimezoneOptions(options);
        } else {
          console.error('Error fetching timezone list:', data.message || 'Unknown error');
        }
      } catch (error) {
        console.error('Failed to fetch timezones:', error);
      } finally {
        setIsLoadingTimezones(false);
      }
    };

    fetchTimezones();
  }, []);

  // Check slug when debounced value changes
  useEffect(() => {
    async function checkSlugAvailability() {
      if (!debouncedSlug || debouncedSlug.length < 3) {
        setSlugStatus('idle');
        return;
      }

      setSlugStatus('checking');

      try {
        const url = `/events/check_slug?slug=${encodeURIComponent(debouncedSlug)}`;
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setSlugStatus(data.valid ? 'valid' : 'invalid');
        } else {
          setSlugStatus('idle');
        }
      } catch (error) {
        console.error('Error checking slug availability:', error);
        setSlugStatus('idle');
      }
    }

    checkSlugAvailability();
  }, [debouncedSlug]);

  useEffect(() => {
    fetch('/artists/list')
      .then((res) => res.json())
      .then((data) => setArtistOptions(data.artists))
      .catch((err) => console.error('Failed to load artists:', err));
  }, []);

  useEffect(() => {
    setIsRecurring(form.values.is_recurring === 'true');

    if (form.values.event_start_date) {
      const date = dayjs(form.values.event_start_date);
      const ordinalIndex = Math.ceil(date.date() / 7) - 1;
      const calculatedOrdinal = ordinalOptions[Math.min(ordinalIndex, ordinalOptions.length - 1)];
      const calculatedDay = dayOptions[date.day()];

      setOrdinalOccurrence(calculatedOrdinal);
      setDayOfWeek(calculatedDay);
    }
  }, [form.values.is_recurring, form.values.event_start_date]);

  const handleAddressChange = (newAddress: string) => {
    form.setFieldValue('address', newAddress);
    setValues((prevValues) => ({
      ...prevValues,
      address: newAddress,
    }));
  };

  useEffect(() => {
    if (!event_frequencies || !Array.isArray(event_frequencies)) {
      console.log('🔍 Skipping useEffect: event_frequencies is missing or invalid');
      return;
    }

    const recurrencePattern = form.values.recurring_event_attributes?.recurrence_pattern?.trim();
    console.log('🔁 Recurrence Pattern selected:', `"${recurrencePattern}"`);

    console.log(
      '📋 Available event_frequencies:',
      event_frequencies.map((f) => `"${f.name}"`),
    );

    let matchedFrequency = event_frequencies.find((f) => f.name === recurrencePattern);

    if (!matchedFrequency) {
      console.log('❌ No match found for recurrence pattern. Checking lowercased match...');

      matchedFrequency = event_frequencies.find((f) => f.name.toLowerCase() === recurrencePattern?.toLowerCase());

      if (matchedFrequency) {
        console.log('⚠️ Found match with case-insensitive comparison:', matchedFrequency.name);
      } else {
        console.log('❌ Still no match even with case-insensitive check.');
      }
    }

    if (matchedFrequency && form.values.event_frequency_id !== matchedFrequency.id) {
      console.log(`🔄 Updating event_frequency_id from ${form.values.event_frequency_id} to ${matchedFrequency.id}`);
      form.setFieldValue('event_frequency_id', matchedFrequency.id);
    } else if (matchedFrequency) {
      console.log('🟰 event_frequency_id is already correctly set.');
    }
  }, [form.values.recurring_event_attributes?.recurrence_pattern, event_frequencies]);

  const [address, setAddress] = useState(form.values.address || '');
  const [timezone, setTimezone] = useState(form.values.timezone || '');

  const [timezoneOptions, setTimezoneOptions] = useState<{ value: string; label: string }[]>([]);
  const [isLoadingTimezones, setIsLoadingTimezones] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingArtists, setIsLoadingArtists] = useState(false);

  const [artistOptions, setArtistOptions] = useState<Artist[]>([]);
  const [artistSearch, setArtistSearch] = useState('');
  const [artistPage, setArtistPage] = useState(1);
  const [hasMoreArtists, setHasMoreArtists] = useState(true);

  const [debouncedSearch] = useDebouncedValue(artistSearch, 300);

  const [croppedImage, setCroppedImage] = useState<string | null>(null);

  const artists = artistOptions;

  const handleSubmit = async (
    submittedValues: typeof form.values & { event_image?: File },
    submitType: 'draft' | 'publish',
  ) => {
    setIsLoading(true);

    // Use event_status_id to determine if updating a draft event (status 3) or creating new
    const isEditing = form.values.event_status_id === 3;
    const url = isEditing ? `/events/${event.slug}?no_redirect=true` : '/events';
    const method = isEditing ? 'PATCH' : 'POST';

    // Run validation based on submitType
    if (submitType === 'draft') {
      if (!form.values.name || form.values.name.trim() === '') {
        showNotification({
          title: 'Validation Error',
          message: 'Event name is required even for drafts.',
          color: 'red',
          icon: <IconAlertCircle />,
          autoClose: 5000,
        });
        setIsLoading(false);
        setOverlayVisible(false);
        return;
      }
    } else {
      const validationErrors = form.validate();
      if (validationErrors.hasErrors) {
        Notifications.clean();
        Object.entries(validationErrors.errors).forEach(([label, message], index) => {
          const formattedLabel = label
            .replace(/_/g, ' ')
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          setTimeout(() => {
            Notifications.show({
              title: `Error with ${formattedLabel}`,
              message: message,
              color: 'red',
              icon: <IconAlertCircle />,
              autoClose: 8000,
              position: 'top-right',
            });
          }, index * 200);
        });
        setIsLoading(false);
        return;
      }
    }

    // If recurring event, set recurring attributes (merging new values)
    if (form.values.is_recurring === 'true' && form.values.recurring_event_occurrences?.length > 0) {
      const firstOccurrence = form.values.recurring_event_occurrences[0];
      form.values.recurring_event_attributes = {
        ...form.values.recurring_event_attributes,
        start_date: form.values.recurring_event_start_date || '',
        start_time: firstOccurrence?.start_time || '',
        end_time: firstOccurrence?.end_time || '',
        recurrence_pattern: form.values.recurrence_pattern || '',
        num_occurrences: parseInt(form.values.num_occurrences || '0', 10),
        re_timezone: form.values.timezone || '',
        spans_next_day: firstOccurrence?.spans_next_day || false,
      };

      if (form.values.recurrence_pattern === 'Monthly') {
        const startDate = new Date(form.values.recurring_event_start_date);
        const dayOfWeek = startDate.toLocaleString('en-US', { weekday: 'long' });
        const dayOfMonth = startDate.getDate();
        const weekNum = Math.ceil(dayOfMonth / 7);
        const ordinals = ['First', 'Second', 'Third', 'Fourth', 'Last'];
        const ordinal = ordinals[Math.min(weekNum - 1, 4)];
        form.values.recurring_event_attributes.occurrence_day = dayOfWeek;
        form.values.recurring_event_attributes.ordinal_occurrence = ordinal;
      }
    }

    // Build FormData merging new form values with existing event values if needed
    const formData = new FormData();

    // Set the submission type flag
    if (submitType === 'draft') {
      formData.append('save_draft', 'true');
    } else {
      formData.append('submit_for_approval', 'true');
      // If updating a draft for approval, explicitly change the event status to 2 (pending)
      if (isEditing) {
        formData.append('event[event_status_id]', '2');
      }
    }

    // Helper function to merge field value: use form value, else fallback to existing event value
    const mergeField = (field: keyof EventData): string =>
      form.values[field] || (event && event[field] ? event[field] : '');

    // Append date and time fields (merging existing values if not modified)
    const startDateStr = mergeField('event_start_date');
    const startTimeStr = mergeField('event_start_time');
    const endDateStr = mergeField('event_end_date');
    const endTimeStr = mergeField('event_end_time');
    if (submitType === 'publish' || (startDateStr && startTimeStr)) {
      if (startDateStr && startTimeStr) {
        const combinedStart = `${startDateStr.split('T')[0]}T${startTimeStr}:00Z`;
        formData.append('event[event_start_date]', combinedStart);
      }
      if (endDateStr && endTimeStr) {
        const combinedEnd = `${endDateStr.split('T')[0]}T${endTimeStr}:00Z`;
        formData.append('event[event_end_date]', combinedEnd);
      }
    }

    // Append timezone, using fallback if necessary
    if (Array.isArray(form.values.timezone)) {
      formData.append('event[timezone]', form.values.timezone[0] || mergeField('timezone'));
    } else {
      formData.append('event[timezone]', form.values.timezone || mergeField('timezone'));
    }

    // Append other required fields (merging with existing values)
    formData.append('event[name]', mergeField('name'));
    if (form.values.slug || event.slug) formData.append('event[slug]', form.values.slug || event.slug);
    formData.append('event[address]', mergeField('address'));

    // Append the description from the editor
    if (editor && typeof editor.getHTML === 'function') {
      formData.append('event[description]', editor.getHTML());
    }

    // Append images: only add if new image file is provided or if a croppedImage exists
    if (form.values.event_image) {
      formData.append('event[event_image]', form.values.event_image);
    }
    if (croppedImage) {
      formData.append('event[cropped_image]', croppedImage);
    }

    // Append optional URL fields
    ['website_url', 'ticket_url', 'facebook_url', 'instagram_url', 'meetup_url', 'tiktok_url'].forEach((field) => {
      const value = form.values[field] || (event && event[field]) || '';
      if (value) formData.append(`event[${field}]`, value);
    });

    // Append boolean fields
    formData.append('event[online_event]', form.values.online_event === 'true' ? 'true' : 'false');
    formData.append('event[is_recurring]', form.values.is_recurring === 'true' ? 'true' : 'false');
    formData.append('event[spans_next_day]', form.values.spans_next_day === 'true' ? 'true' : 'false');

    // Append custom occurrences if applicable
    const finalOccurrences: { date: string; start_time: string; end_time: string; spans_next_day: boolean }[] =
      form.values.recurring_event_occurrences || [];
    if (form.values.is_recurring === 'true' && finalOccurrences.length > 0) {
      formData.append('event[use_custom_occurrences]', 'true');
      finalOccurrences.forEach((occurrence, index) => {
        formData.append(`event[custom_occurrences][${index}][date]`, occurrence.date);
        formData.append(`event[custom_occurrences][${index}][start_time]`, occurrence.start_time);
        formData.append(`event[custom_occurrences][${index}][end_time]`, occurrence.end_time);
        formData.append(`event[custom_occurrences][${index}][spans_next_day]`, occurrence.spans_next_day.toString());
      });
    }

    // Append arrays for event types, dance styles, artists, and organizers
    if (Array.isArray(form.values.event_type_ids)) {
      form.values.event_type_ids.forEach((id) => formData.append('event[event_type_ids][]', id.toString()));
    }
    if (Array.isArray(form.values.dance_style_ids)) {
      form.values.dance_style_ids.forEach((id) => formData.append('event[dance_style_ids][]', id.toString()));
    }
    if (Array.isArray(form.values.artistIds)) {
      form.values.artistIds.forEach((id) => formData.append('event[artist_ids][]', id.toString()));
    }
    if (Array.isArray(form.values.organizer_ids)) {
      form.values.organizer_ids.forEach((id) => formData.append('event[organizer_ids][]', id.toString()));
    }

    // Append recurring event attributes if applicable
    if (form.values.is_recurring === 'true' && form.values.recurring_event_attributes) {
      Object.entries(form.values.recurring_event_attributes).forEach(([key, value]) => {
        formData.append(`event[recurring_event_attributes][${key}]`, value?.toString() || '');
      });
    }

    // Get CSRF token and send the request
    try {
      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
      if (!csrfToken) {
        console.error('CSRF token not found');
        setIsLoading(false);
        setOverlayVisible(false);
        return;
      }

      const response = await fetch(url, {
        method,
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
        body: formData,
      });

      if (response.ok) {
        let data = {};
        if (submitType !== 'draft') {
          try {
            data = await response.json();
          } catch (e) {
            console.log('Response was not JSON, continuing with redirect...');
          }
        }
        const eventSlug = data.slug || form.values.slug;
        let successMessage = '';
        if (submitType === 'draft') {
          successMessage = 'Event draft was saved successfully!';
        } else if (form.values.is_recurring === 'true') {
          const pattern = form.values.recurrence_pattern || 'regular';
          const occurrences = form.values.num_occurrences || form.values.recurring_event_occurrences?.length || '?';
          successMessage = `Successfully submitted ${pattern} recurring event with ${occurrences} occurrences!`;
        } else {
          successMessage = 'Event was submitted successfully!';
        }
        showNotification({
          title: submitType === 'draft' ? 'Draft Saved' : 'Submission Successful',
          message: successMessage,
          color: 'green',
          icon: <IconCheck />,
          autoClose: 6000,
          position: 'top-right',
        });

        // Redirect accordingly
        if (submitType === 'draft') {
          const username = user?.username;
          setTimeout(() => (window.location.href = `/users/${username}/managed-events?active_tab=draft`), 1500);
        } else if (form.values.is_recurring === 'true' && eventSlug) {
          setTimeout(() => (window.location.href = `/re/${eventSlug}`), 1500);
        } else {
          setTimeout(() => (window.location.href = `/events/${eventSlug}`), 1500);
        }
      } else {
        setOverlayVisible(false);
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          try {
            const data = await response.json();
            console.log('Server error response:', data);
            if (data.errors) {
              Notifications.clean();
              Object.entries(data.errors).forEach(([field, message], index) => {
                const formattedField = field
                  .replace(/_/g, ' ')
                  .split(' ')
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
                form.setFieldError(field, Array.isArray(message) ? message[0] : message);
                setTimeout(() => {
                  Notifications.show({
                    title: `Error with ${formattedField}`,
                    message: Array.isArray(message) ? message[0] : message,
                    color: 'red',
                    icon: <IconAlertCircle />,
                    autoClose: 8000,
                    position: 'top-right',
                  });
                }, index * 200);
              });
            } else if (data.error) {
              showNotification({
                title: 'Submission Failed',
                message: data.error,
                color: 'red',
                icon: <IconAlertCircle />,
                autoClose: 5000,
                position: 'top-right',
              });
            } else {
              showNotification({
                title: 'Submission Failed',
                message: 'An error occurred. Please check all fields and try again.',
                color: 'red',
                icon: <IconAlertCircle />,
                autoClose: 5000,
                position: 'top-right',
              });
            }
          } catch (e) {
            console.error('Error parsing JSON response:', e);
            showNotification({
              title: 'Submission Failed',
              message: 'Could not process server response.',
              color: 'red',
              icon: <IconAlertCircle />,
              autoClose: 5000,
              position: 'top-right',
            });
          }
        } else {
          try {
            const textResponse = await response.text();
            console.log('Non-JSON response:', textResponse.substring(0, 200));
            showNotification({
              title: 'Submission Failed',
              message: `Server returned status ${response.status}: ${response.statusText}`,
              color: 'red',
              icon: <IconAlertCircle />,
              autoClose: 5000,
              position: 'top-right',
            });
          } catch (e) {
            console.error('Error reading response text:', e);
            showNotification({
              title: 'Submission Failed',
              message: 'Could not read server response.',
              color: 'red',
              icon: <IconAlertCircle />,
              autoClose: 5000,
              position: 'top-right',
            });
          }
        }
      }
    } catch (error) {
      setOverlayVisible(false);
      showNotification({
        title: 'Unexpected Error',
        message: 'An unexpected error occurred. Please try again.',
        color: 'red',
        icon: <IconAlertCircle />,
        autoClose: 5000,
        position: 'top-right',
      });
      console.error('Error creating/updating event:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Heading.configure({ levels: [1, 2, 3] }),
      Link.configure({ openOnClick: true }),
    ],
    content: form.values.description,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();

      form.setFieldValue('description', html);
    },
  });

  const statusMapping: Record<number, { status: string; color: string; icon: JSX.Element }> = {
    1: { status: 'Active', color: 'green', icon: <IconCheck size={16} /> },
    2: { status: 'Pending', color: 'yellow', icon: <IconAlertTriangle size={16} /> },
    3: { status: 'Draft', color: 'blue', icon: <IconInfoCircle size={16} /> },
  };

  const eventStatus =
    event.event_status_id && statusMapping[event.event_status_id]
      ? statusMapping[event.event_status_id]
      : { status: 'None', color: 'gray', icon: <IconInfoCircle size={16} /> };

  return (
    <MantineProvider theme={theme}>
      <Notifications />
      <Container size="sm" style={{ minHeight: '75vh' }} px={isMobile ? 0 : undefined}>
        <Paper
          p={isMobile ? '0.5rem' : 'lg'}
          shadow="xs"
          mt="lg"
          mb="8rem"
          style={{ borderRadius: '8px', backgroundColor: '#3d3c3e' }}
          pos="relative"
        >
          {event.event_status_id ? (
            <Alert
              icon={eventStatus.icon}
              title={`Event Status: ${eventStatus.status}`}
              color={eventStatus.color}
              variant="filled"
              mb="md"
            ></Alert>
          ) : null}
          <Title order={3} c="#E4E4E7" mt="md" mx="sm">
            Create New Event
          </Title>
          <form>
            <Accordion multiple className="form-accordion">
              <Accordion.Item value="event-info">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconCalendarPlus size={28} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Event Host & Name</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <MultiSelect
                    label={
                      <Group gap={4} align="center">
                        <Text className="mb-2">Event Host</Text>
                        <Tooltip
                          label="Organizers you're linked to will appear here. If the list is empty, you can continue filling out the form and submit the event. Afterwards, create an organizer profile and return later to add the organizer to this event."
                          multiline
                          w={220}
                          withArrow
                          position="bottom"
                          color="gray.9"
                          transitionProps={{ duration: 200 }}
                          opened={tooltipOpenedOrganizer}
                          onClose={() => setTooltipOpenedOrganizer(false)}
                          onOpen={() => setTooltipOpenedOrganizer(true)}
                        >
                          <ActionIcon
                            variant="transparent"
                            size="sm"
                            className="mb-2"
                            onClick={() => setTooltipOpenedOrganizer((prev) => !prev)}
                            style={{ cursor: 'pointer' }}
                          >
                            <IconInfoCircle size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    }
                    placeholder={
                      form.values.organizer_ids && form.values.organizer_ids.length > 0 ? '' : 'Select event organizers'
                    }
                    name="organizer_ids"
                    hidePickedOptions
                    comboboxProps={{ shadow: 'xl' }}
                    data={filteredOrganizers.map((org) => ({
                      value: org.id.toString(),
                      label: org.name,
                    }))}
                    value={(form.values.organizer_ids || []).map((id) => id.toString())}
                    onChange={(values) => {
                      const numericValues = Array.isArray(values) ? values.map((v) => Number(v)) : [];
                      form.setFieldValue('organizer_ids', numericValues);
                    }}
                    searchable
                    clearable
                    mt="md"
                    styles={{
                      label: { color: '#FFF7ED', fontSize: '16px' },
                      input: { fontSize: '16px' },
                      dropdown: { fontSize: '16px' },
                    }}
                  />

                  <TextInput
                    label="Event Name"
                    name="name"
                    placeholder="Enter the name"
                    {...form.getInputProps('name')}
                    onChange={handleEventNameChange}
                    mt="md"
                    required
                    styles={{ label: { color: '#FFF7ED' } }}
                  />

                  <TextInput
                    label="Slug"
                    name="slug"
                    value={slug}
                    placeholder="your-event-slug"
                    {...form.getInputProps('slug', {
                      onChange: (e: React.ChangeEvent<HTMLInputElement>) => setSlug(e.target.value),
                    })}
                    mt="md"
                    required
                    styles={{ label: { color: '#FFF7ED' } }}
                  />
                  {slugStatus === 'checking' && (
                    <Text size="sm" c="gray">
                      Checking availability...
                    </Text>
                  )}
                  {slugStatus === 'valid' && (
                    <Text size="sm" c="green">
                      Slug is available.
                    </Text>
                  )}
                  {slugStatus === 'invalid' && (
                    <Text size="sm" c="red">
                      Slug is already taken.
                    </Text>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Accordion multiple className="form-accordion">
              <Accordion.Item value="location-info">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconMapPinFilled size={28} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Location Info</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <Text size="sm" fw={500} mt={3} style={{ color: '#FFF7ED' }}>
                    Is this an online event or in-person event?
                  </Text>
                  <SegmentedControl
                    name="online_event"
                    color="orange"
                    transitionDuration={300}
                    transitionTimingFunction="linear"
                    size="sm"
                    data={[
                      { label: 'In Person', value: 'false' },
                      { label: 'Online', value: 'true' },
                    ]}
                    {...form.getInputProps('online_event')}
                    style={{
                      border: '2px solid orange',
                      backgroundColor: 'white',
                    }}
                  />

                  {form.values.online_event !== 'true' && (
                    <AddressAutocomplete
                      value={address}
                      onChange={(newAddress) => {
                        setAddress(newAddress);
                        form.setFieldValue('address', newAddress);
                      }}
                      onTimezoneSelect={(newTimezone) => {
                        setTimezone(newTimezone);
                        form.setFieldValue('timezone', newTimezone);
                      }}
                      timezoneValue={timezone}
                    />
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Accordion multiple className="form-accordion">
              <Accordion.Item value="date-info">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconCalendarTime size={28} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Date & Time Info</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <SegmentedControl
                    name="is_recurring"
                    color="orange"
                    transitionDuration={500}
                    transitionTimingFunction="linear"
                    size="sm"
                    data={[
                      { label: 'Single Event', value: 'false' },
                      { label: 'Recurring Event', value: 'true' },
                    ]}
                    {...form.getInputProps('is_recurring')}
                    style={{
                      border: '2px solid orange',
                      backgroundColor: 'white',
                    }}
                  />

                  {isRecurring && <RecurringEventFields form={form} />}

                  <div style={{ display: isRecurring ? 'none' : 'block' }}>
                    <Group gap="md" mt="md" grow>
                      <DatePickerInput
                        label="Event Start Date"
                        placeholder="Select date"
                        name="event_start_date"
                        value={
                          form.values.event_start_date
                            ? (() => {
                                // If the value is just a date string (YYYY-MM-DD), parse it properly in local timezone
                                if (
                                  form.values.event_start_date.length === 10 &&
                                  form.values.event_start_date.includes('-')
                                ) {
                                  // Create date at noon to avoid any timezone boundary issues
                                  const [year, month, day] = form.values.event_start_date.split('-').map(Number);
                                  const date = new Date(year, month - 1, day, 12, 0, 0);

                                  return date;
                                } else {
                                  // Otherwise, use the standard Date constructor
                                  const date = new Date(form.values.event_start_date);
                                  return date;
                                }
                              })()
                            : null
                        }
                        onChange={(date) => {
                          if (date) {
                            const localDate = new Date(date);
                            localDate.setHours(0, 0, 0, 0);

                            // Get the date components directly from the local date object
                            const year = localDate.getFullYear();
                            const month = String(localDate.getMonth() + 1).padStart(2, '0');
                            const day = String(localDate.getDate()).padStart(2, '0');
                            const dateString = `${year}-${month}-${day}`;

                            // Store only the date portion, without time information
                            form.setFieldValue('event_start_date', dateString);

                            // Also copy the same date to the end date
                            form.setFieldValue('event_end_date', dateString);
                          } else {
                            form.setFieldValue('event_start_date', '');
                            // Optionally clear end date when start date is cleared
                            // form.setFieldValue('event_end_date', '');
                          }
                          form.validateField('event_end_date');
                          form.validateField('event_end_time');
                        }}
                        required={!isDraft}
                        styles={{ label: { color: '#FFF7ED' } }}
                      />

                      <TimeInput
                        label="Event start time"
                        name="event_start_time"
                        ref={startTimeRef}
                        rightSection={startPickerControl}
                        color="orange"
                        value={form.values.event_start_time}
                        onChange={(e) => {
                          handleStartTimeChange(e);

                          form.validateField('event_end_date');
                          form.validateField('event_end_time');
                        }}
                        required={!isDraft}
                        styles={{ label: { color: '#FFF7ED' } }}
                      />
                    </Group>

                    <Group gap="md" mt="md" grow>
                      <DatePickerInput
                        label="Event End Date"
                        name="event_end_date"
                        placeholder="Select date"
                        value={
                          form.values.event_end_date
                            ? (() => {
                                // If the value is just a date string (YYYY-MM-DD), parse it properly in local timezone
                                if (
                                  form.values.event_end_date.length === 10 &&
                                  form.values.event_end_date.includes('-')
                                ) {
                                  // Create date at noon to avoid any timezone boundary issues
                                  const [year, month, day] = form.values.event_end_date.split('-').map(Number);
                                  const date = new Date(year, month - 1, day, 12, 0, 0);

                                  return date;
                                } else {
                                  // Otherwise, use the standard Date constructor
                                  const date = new Date(form.values.event_end_date);
                                  return date;
                                }
                              })()
                            : null
                        }
                        onChange={(date) => {
                          if (date) {
                            const localDate = new Date(date);
                            localDate.setHours(0, 0, 0, 0);

                            // Get the date components directly from the local date object
                            const year = localDate.getFullYear();
                            const month = String(localDate.getMonth() + 1).padStart(2, '0');
                            const day = String(localDate.getDate()).padStart(2, '0');
                            const dateString = `${year}-${month}-${day}`;

                            form.setFieldValue('event_end_date', dateString);
                          } else {
                            form.setFieldValue('event_end_date', '');
                          }
                          form.validateField('event_end_date');
                          form.validateField('event_end_time');
                        }}
                        required={!isDraft}
                        styles={{ label: { color: '#FFF7ED' } }}
                      />
                      <TimeInput
                        label="Event end time"
                        name="event_end_time"
                        ref={endTimeRef}
                        rightSection={endPickerControl}
                        color="orange"
                        value={form.values.event_end_time}
                        onChange={(e) => {
                          handleEndTimeChange(e);

                          form.validateField('event_end_date');
                          form.validateField('event_end_time');
                        }}
                        required={!isDraft}
                        styles={{ label: { color: '#FFF7ED' } }}
                      />
                    </Group>
                  </div>

                  {(form.errors.event_end_date || form.errors.event_end_time) && (
                    <Alert variant="filled" color="red" mt="md" icon={<IconAlertCircle size={16} />}>
                      {form.errors.event_end_date && <div>{form.errors.event_end_date}</div>}
                      {form.errors.event_end_time && <div>{form.errors.event_end_time}</div>}
                    </Alert>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Accordion multiple className="form-accordion">
              <Accordion.Item value="event-details">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconList size={28} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Event Details</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <ImageUploadModal previewUrl={seoImagePreview} onImageChange={handleImageChange} />

                  <MultiSelect
                    label="What type of event is this?"
                    comboboxProps={{ shadow: 'md' }}
                    name="event_type_ids"
                    hidePickedOptions
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder={
                      form.values.event_type_ids && form.values.event_type_ids.length > 0
                        ? ''
                        : 'Choose your event types'
                    }
                    data={sortedEventTypes.map((style) => ({
                      value: style.id.toString(),
                      label: style.name,
                    }))}
                    value={
                      // Ensure we always have an array and convert numbers to strings
                      (Array.isArray(form.values.event_type_ids) ? form.values.event_type_ids : []).map((id) =>
                        id.toString(),
                      )
                    }
                    onChange={(values) => {
                      // Convert the selected string IDs back to numbers
                      const numericValues = values.map(Number);
                      form.setFieldValue('event_type_ids', numericValues);
                    }}
                    searchable
                    clearable
                    required={!isDraft}
                  />

                  <MultiSelect
                    label="Dance Styles"
                    name="dance_style_ids"
                    comboboxProps={{ shadow: 'md' }}
                    hidePickedOptions
                    className="select-dropdown"
                    styles={{
                      label: { color: '#FFF7ED' },
                      dropdown: { zIndex: 1000 }, // ✅ Increase dropdown z-index
                    }}
                    placeholder={
                      form.values.dance_style_ids && form.values.dance_style_ids.length > 0 ? '' : 'Choose dance styles'
                    }
                    data={sortedDanceStyles.map((style) => ({
                      value: style.id.toString(),
                      label: style.name,
                    }))}
                    value={(Array.isArray(form.values.dance_style_ids) ? form.values.dance_style_ids : []).map((id) =>
                      id.toString(),
                    )}
                    onChange={(values) => {
                      // Convert the selected string IDs back to numbers
                      const numericValues = values.map(Number);
                      form.setFieldValue('dance_style_ids', numericValues);
                    }}
                    searchable
                    clearable
                    mt="lg"
                    required={!isDraft}
                  />

                  <Select
                    label="Event Frequency"
                    name="event_frequency_id"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Select event frequency"
                    data={sortedEventFrequencies.map((freq) => ({
                      value: freq.id.toString(),
                      label: freq.name,
                    }))}
                    value={form.values.event_frequency_id ? form.values.event_frequency_id.toString() : null}
                    onChange={(value) => {
                      form.setFieldValue('event_frequency_id', value ? Number(value) : null);
                      // Validate this field immediately after change
                      form.validateField('event_frequency_id');
                    }}
                    onBlur={() => form.validateField('event_frequency_id')}
                    error={form.errors.event_frequency_id}
                    searchable
                    clearable
                    mt="lg"
                    mb="xl"
                    required={!isDraft}
                  />

                  <Text size="sm" fw={500} style={{ color: '#FFF7ED' }} required>
                    Event Description
                  </Text>
                  {editor && (
                    <RichTextEditor editor={editor} mt="" className="rich-text-editor">
                      <RichTextEditor.Toolbar>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Bold />
                          <RichTextEditor.Italic />
                          <RichTextEditor.Underline />
                          <RichTextEditor.Strikethrough />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Code />
                          <RichTextEditor.Highlight />
                          <RichTextEditor.ClearFormatting />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Link />
                          <RichTextEditor.Unlink />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.H1 />
                          <RichTextEditor.H2 />
                          <RichTextEditor.H3 />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Blockquote />
                          <RichTextEditor.Hr />
                          <RichTextEditor.BulletList />
                          <RichTextEditor.OrderedList />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Blockquote />
                          <RichTextEditor.OrderedList />
                          <RichTextEditor.BulletList />
                        </RichTextEditor.ControlsGroup>
                        <RichTextEditor.ControlsGroup>
                          <RichTextEditor.Undo />
                          <RichTextEditor.Redo />
                        </RichTextEditor.ControlsGroup>
                      </RichTextEditor.Toolbar>
                      <RichTextEditor.Content />
                    </RichTextEditor>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Accordion multiple className="form-accordion">
              <Accordion.Item value="artist-info">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconUsersGroup size={28} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Artists</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <MultiSelect
                    label={
                      <Group gap={4} align="center">
                        <Text className="mb-2">Select Artists</Text>
                        <Tooltip
                          label="Only artists that have created an artist profile will appear here..."
                          multiline
                          w={220}
                          withArrow
                          position="bottom"
                          color="gray.9"
                        >
                          <ActionIcon
                            variant="transparent"
                            size="sm"
                            className="mb-2"
                            style={{ cursor: 'pointer' }}
                            onClick={() => setTooltipOpened((prev) => !prev)}
                          >
                            <IconInfoCircle size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    }
                    name="artistIds"
                    hidePickedOptions
                    disabled={isLoadingArtists}
                    comboboxProps={{ shadow: 'md' }}
                    placeholder={form.values.artistIds && form.values.artistIds.length > 0 ? '' : 'Choose artists'}
                    styles={{
                      label: { color: '#FFF7ED', fontSize: '16px' },
                      input: { fontSize: '16px' },
                      dropdown: { fontSize: '16px' },
                    }}
                    data={artistOptions.map((artist) => ({
                      value: artist.id.toString(),
                      label: artist.name,
                    }))}
                    value={(form.values.artistIds || []).map(String)}
                    onChange={(values) => {
                      const numericValues = Array.isArray(values) ? values.map(Number) : [];
                      form.setFieldValue('artistIds', numericValues);
                    }}
                    searchable
                    clearable
                    onDropdownOpen={() => {
                      if (!artistOptionsLoaded) fetchAllArtists();
                    }}
                  />

                  {/* Display selected artist avatars below */}
                  {form.values.artistIds.length > 0 && (
                    <Stack mt="md">
                      <Group style={{ gap: '4px', flexWrap: 'wrap' }}>
                        {form.values.artistIds.map((id: Key | null | undefined) => {
                          const artist = artists.find((a) => a.id === id);
                          return artist ? (
                            <Avatar
                              key={id}
                              src={artist.avatar_url}
                              alt={artist.name}
                              radius="50%"
                              size="xl"
                              style={{
                                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
                                border: '2px solid white',
                              }}
                            />
                          ) : null;
                        })}
                      </Group>
                    </Stack>
                  )}
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            <Accordion multiple className="form-accordion">
              <Accordion.Item value="website-links">
                <Accordion.Control>
                  <Group gap="xs">
                    <IconLink size={20} />
                    <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Links</Text>
                  </Group>
                </Accordion.Control>
                <Accordion.Panel>
                  <TextInput
                    label="Website Link"
                    name="website_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the event website."
                    {...form.getInputProps('website_url')}
                    mt="md"
                  />

                  <TextInput
                    label="Passes Link"
                    name="ticket_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to purchase passes."
                    {...form.getInputProps('ticket_url')}
                    mt="md"
                  />

                  <TextInput
                    label="Facebook Event Link"
                    name="facebook_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to Facebook event."
                    {...form.getInputProps('facebook_url')}
                    mt="md"
                  />

                  <TextInput
                    label="Instagram Link"
                    name="instagram_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to the event Instagram page."
                    {...form.getInputProps('instagram_url')}
                    mt="md"
                  />

                  <TextInput
                    label="Meetup Link"
                    name="meetup_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to Meetup event."
                    {...form.getInputProps('meetup_url')}
                    mt="md"
                  />

                  <TextInput
                    label="Tiktok Link"
                    name="tiktok_url"
                    styles={{ label: { color: '#FFF7ED' } }}
                    placeholder="Enter the link to the event Tiktok event page."
                    {...form.getInputProps('tiktok_url')}
                    mt="md"
                  />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            {/* Example error display */}
            {errors && Object.keys(errors).length > 0 && (
              <Box color="red" mt="md">
                {Object.entries(errors).map(([field, messages]) => (
                  <div key={field}>
                    {field}: {messages.join(', ')}
                  </div>
                ))}
              </Box>
            )}

            <Group mt="xl">
              <Button
                variant="outline"
                size="lg"
                fullWidth
                color="gray"
                loading={isLoading}
                onClick={() => handleSubmit(form.values, 'draft')}
              >
                Save as Draft
              </Button>

              <Button
                type="button"
                size="lg"
                fullWidth
                color="#EA580C"
                loading={isLoading}
                onClick={() => handleSubmit(form.values, 'publish')}
              >
                {form.values.is_recurring === 'true'
                  ? 'Submit Recurring Event For Approval'
                  : 'Submit Event For Approval'}
              </Button>
            </Group>
          </form>
        </Paper>
      </Container>
    </MantineProvider>
  );
}
